/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import socket, { acompanharPedido } from '../lib/socket';
import { capturePaymentError, captureNetworkError } from '../services/error-tracking.service';
import { finalDoPedido } from '../utils/redact-link-credentials';
import { sendPurchaseToN8n } from '../utils/n8n-webhook';
import { getMetaClickIds } from '../utils/metaClickIds';
import { trackMetaPurchase } from '../utils/metaPurchase';
import { isInApp, postToApp } from '../utils/inApp';
import type { TermosAssinatura } from '../utils/termos-assinatura';
import { PrecoDoRodape, QuadroDeTermos } from './TermosAssinatura';

// Função para validar CPF
const validateCPF = (cpf: string): boolean => {
  // Remove caracteres não numéricos
  const cleanCPF = cpf.replace(/\D/g, '');

  // Verifica se tem 11 dígitos
  if (cleanCPF.length !== 11) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;

  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(10))) return false;

  return true;
};

// Função para aplicar máscara de CPF
const maskCPF = (value: string): string => {
  // Remove tudo que não é dígito
  const numbers = value.replace(/\D/g, '');

  // Aplica a máscara
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// Helper para parsear preço formatado "R$ 69,90" -> 69.90
const parseFormattedPrice = (priceStr: string): number => {
  return parseFloat(priceStr.replace(/[^\d,]/g, '').replace(',', '.'));
};

// Validação simples de e-mail (inline no checkout)
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

interface PaymentsPixProps {
  clientId: string;
  planId: string;
  coupon: {
    code: string;
    discount_type: 'PERCENT' | 'AMOUNT';
    value: number;
  } | null;
  price: {
    total: string;
    cupom: boolean;
    /** Linha pequena sob o total na barra do celular (ex.: "por mês"). */
    detalhe?: string;
  };
  /**
   * Termos da assinatura (utils/termos-assinatura): quadro acima do botão de
   * gerar o PIX e o valor/frequência da barra do celular, no lugar de
   * total/detalhe. Só exibição — `price.total` segue alimentando purchase/GTM/n8n.
   */
  termos?: TermosAssinatura;
  // Refactor client→sub: IDs dos children dessa sessão de checkout
  childIds?: string[];
  // Refactor client→sub: dados do pagador/endereço dessa sessão
  recipient?: {
    name?: string;
    cpf?: string;
    whatsapp?: string;
  };
  shipping?: {
    cep?: string;
    rua?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    recebedor?: string;
  };
  email: string;
  onEmailChange: (value: string) => void;
  ensureEmailSaved: (email: string) => Promise<string>;
  // Renovação self-service (/renovar/:token). Ausente = compra normal do checkout.
  renewal?: {
    token: string;
  };
}

const PaymentsPix: React.FC<PaymentsPixProps> = ({ clientId: clientIdProp, planId, price, coupon, childIds, recipient, shipping, email, onEmailChange, ensureEmailSaved, renewal, termos }) => {
  const [orderId, setOrderId] = useState<string | null>(null);
  // {orderId, nonce} da criação do PIX com source='app' — troca por token
  // de auto-login quando aprovar (Task seguinte). Só em memória (padrão do checkout).
  const [appSession, setAppSession] = useState<{ orderId: string; nonce: string } | null>(null);

  // Disparo único: polling e WebSocket podem confirmar de novo antes do app
  // fechar a WebView — o postMessage não pode duplicar.
  const completedRef = useRef(false);

  // #391: aprovação chega pelo polling E pelo WebSocket — tracking e redirect uma vez só.
  const approvedRef = useRef(false);
  // #391: valor do Purchase do pixel = `amount` do status (o payment que o servidor
  // manda pela CAPI). O WebSocket não traz valor: vale o último poll desta order.
  const pixAmountRef = useRef<{ orderId: string; amount: unknown } | null>(null);

  // Purchase do pixel com o mesmo event_id da CAPI. Renovação não dispara (o
  // servidor também não conta); PIX só chega aqui aprovado, então recusa não dispara.
  const trackPixPurchase = (amount: unknown) => {
    if (renewal || !orderId) return;
    trackMetaPurchase({ appmaxOrderId: orderId, value: amount });
  };

  // #391: 1s antes de sair da página — a requisição do pixel (e da tag do GTM)
  // disparada no mesmo tick pode ser cortada pela navegação.
  const redirectToApproved = () => {
    setTimeout(() => {
      window.location.href = 'https://coracoespreciosos.com.br/compra-aprovada/';
    }, 1000);
  };

  // Tela do QR fica parada até o app fechar a WebView (a troca de token pode levar
  // até o timeout do axios) — sem isso o usuário que já pagou vê o QR intocado.
  const [inAppCompleting, setInAppCompleting] = useState(false);

  // Aprovado dentro do app: troca o nonce pelo token de auto-login e devolve o
  // controle pro app via postMessage. Sem redirect WordPress — o container é o app.
  // Falha na troca NÃO bloqueia: postMessage sem token → app orienta primeiro acesso.
  const completeInApp = async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setInAppCompleting(true);
    let loginToken: string | undefined;
    try {
      if (appSession) {
        const res = await api.post('/payments/app-login-token', appSession);
        loginToken = (res.data?.data ?? res.data)?.token;
      }
    } catch (err) {
      // sem token → app cai no primeiro acesso JIT
      captureNetworkError(err, '/payments/app-login-token', 'POST', {
        component: 'PaymentsPix',
        action: 'app_login_token_exchange',
      });
    }
    postToApp({ type: 'purchase-complete', loginToken, email });
  };

  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [cpfTouched, setCpfTouched] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  // 🔒 PROTEÇÃO CONTRA MÚLTIPLOS CLIQUES COM LOCALSTORAGE
  const [isProcessing, setIsProcessing] = useState(() => {
    const stored = localStorage.getItem('payment_processing_pix');
    if (stored) {
      const { timestamp, planId: planoDaTrava } = JSON.parse(stored);
      const now = Date.now();
      // Se passou mais de 2 minutos, considera expirado
      if (now - timestamp < 120000) {
        // A trava é do PIX de UM plano: na renovação, trocar de plano remonta o
        // componente e ele não pode nascer travado pelo PIX do plano anterior.
        return !planoDaTrava || planoDaTrava === planId;
      }
      localStorage.removeItem('payment_processing_pix');
    }
    return false;
  });
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Trava restaurada do localStorage (página recarregada): o timer de 2 minutos
  // morreu com a montagem anterior. Sem este o botão ficava em "Gerando Pix..."
  // até outro recarregar depois do prazo.
  const travaRestauradaRef = useRef(isProcessing);
  useEffect(() => {
    if (!travaRestauradaRef.current) return;
    const stored = localStorage.getItem('payment_processing_pix');
    const restante = stored ? 120000 - (Date.now() - JSON.parse(stored).timestamp) : 0;
    const timer = setTimeout(() => {
      setIsProcessing(false);
      localStorage.removeItem('payment_processing_pix');
    }, Math.max(0, restante));
    processingTimeoutRef.current = timer;
    return () => clearTimeout(timer);
  }, []);

  // Cupom da ÚLTIMA renderização: a resposta de um PIX pedido antes da troca do
  // cupom compara com ele e não repõe na tela o QR do valor antigo.
  const cupomNaTelaRef = useRef<string | null>(null);
  cupomNaTelaRef.current = coupon?.code ?? null;

  // Pedidos de PIX que o cliente VIU nesta tela e saíram de cena quando o cupom
  // mudou (os 3 mais recentes). Seguem vigiados: ver o efeito de `pedidosAnteriores`.
  const [pedidosAnteriores, setPedidosAnteriores] = useState<string[]>([]);
  const lembrarPedidoAnterior = (id: string) =>
    setPedidosAnteriores((lista) => (lista.includes(id) ? lista : [...lista, id].slice(-3)));
  const formRenderedAtRef = useRef<number>(Date.now()); // ⏱️ Anti-bot
  const MIN_FILL_TIME = 3000; // 3 segundos (PIX só pede CPF, mais rápido que cartão)

  const [pixCode, setPixCode] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [erroPix, setErroPix] = useState<string | null>(null);
  const [duplicateNotice, setDuplicateNotice] = useState<string | null>(null);

  const [utmData, setUtmData] = useState({
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
  });

  // Validação de CPF
  const isCpfValid = validateCPF(cpf);
  const showCpfError = cpfTouched && cpf && !isCpfValid;

  // Função para lidar com mudança no CPF
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maskedValue = maskCPF(value);
    setCpf(maskedValue);
    setCpfError('');
  };

  // Função para lidar com blur no CPF
  const handleCpfBlur = () => {
    setCpfTouched(true);
    if (cpf && !isCpfValid) {
      setCpfError('CPF inválido. Digite um CPF válido.');
    }
  };

  // Função para obter classe CSS do input CPF
  const getCpfInputClassName = () => {
    if (showCpfError) {
      return 'border-2 border-red-500 focus:border-red-500 focus:ring-red-500 focus:ring-2';
    }
    if (cpf && isCpfValid) {
      return 'border-2 border-green-500 focus:border-green-500 focus:ring-green-500 focus:ring-2';
    }
    return 'border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-500 focus:ring-2';
  };

  const generatePix = async () => {
    // 🔒 PROTEÇÃO CONTRA MÚLTIPLOS CLIQUES
    if (isProcessing) {
      console.warn('⚠️ PIX já está sendo gerado. Ignorando clique duplicado.');
      return;
    }

    if (!isCpfValid) {
      setCpfError('CPF inválido. Digite um CPF válido.');
      return;
    }

    // ⏱️ ANTI-BOT: rejeitar se formulário preenchido muito rápido
    const fillTime = Date.now() - formRenderedAtRef.current;
    if (fillTime < MIN_FILL_TIME) {
      console.warn('🤖 Preenchimento muito rápido:', fillTime, 'ms');
      alert('Por favor, aguarde um momento antes de gerar o PIX.');
      return;
    }

    // Salva o e-mail no cliente (com dedup) antes de gerar o PIX e usa o clientId resolvido
    if (!isValidEmail(email)) {
      setEmailError('Digite um e-mail válido para receber as informações.');
      return;
    }
    // Na renovação o dono já é conhecido (vem do token) e o e-mail é só leitura:
    // salvar de novo poderia trocar o clientId em voo pelo dedup e derrubar a
    // validação do link no backend.
    let clientId = clientIdProp;
    if (!renewal) {
      try {
        clientId = await ensureEmailSaved(email);
      } catch (saveError: unknown) {
        // 409 (#362): cadastro com assinatura não troca o e-mail pelo checkout — a
        // mensagem do backend diz o que fazer; "tente novamente" só daria o mesmo erro.
        const axiosError = saveError as import('axios').AxiosError<{ message?: string }>;
        setEmailError(
          axiosError?.response?.status === 409 && axiosError.response.data?.message
            ? axiosError.response.data.message
            : 'Não foi possível salvar o e-mail. Tente novamente.',
        );
        return;
      }
    }
    setEmailError(null);
    setErroPix(null);

    setLoading(true);
    setIsProcessing(true);

    // 💾 Salva estado no localStorage
    localStorage.setItem(
      'payment_processing_pix',
      JSON.stringify({
        timestamp: Date.now(),
        clientId,
        planId,
      }),
    );

    // 🔒 Timeout de segurança: libera após 2 minutos
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
    processingTimeoutRef.current = setTimeout(() => {
      console.warn('⚠️ Timeout de segurança PIX: liberando após 2 minutos');
      setIsProcessing(false);
      localStorage.removeItem('payment_processing_pix');
    }, 120000);

    const cupomDoPedido = coupon?.code ?? null;
    try {
      const cleanCPF = cpf.replace(/\D/g, '');

      // IP do comprador: resolvido no backend a partir do request (#133) —
      // antes vinha do api.ipify.org, terceiro no caminho crítico da compra.
      const res = await api.post('/payments/pix', {
        clientId: clientId,
        planId: planId,
        coupon,
        cpf: cleanCPF,
        childIds, // Refactor: vincula esses children à sub criada via webhook
        // Refactor: AppMax customer + order snapshot usam dados do form atual
        // (mesclamos CPF local — coletado neste componente)
        recipient: { ...recipient, cpf: cleanCPF },
        shipping,
        ...utmData,
        ...(renewal && { renewalToken: renewal.token }),
        ...(isInApp() && { source: 'app' }),
        ...getMetaClickIds(), // #389: fbc/fbp pro Purchase da CAPI (PIX aprova no webhook)
      });

      // A API retorna { success: true, data: { orderId, qrCode, code, expiration } }
      const pixData = res.data.data || res.data;

      // Cupom trocado com o pedido em voo: este QR é do valor antigo. O efeito
      // do cupom já limpou a tela; aqui só não repõe o QR velho e deixa o botão
      // solto para gerar no valor que a página mostra.
      if (cupomNaTelaRef.current !== cupomDoPedido) {
        setIsProcessing(false);
        localStorage.removeItem('payment_processing_pix');
        return;
      }

      // Backend devolve o MESMO PIX quando há geração recente (anti-duplicata;
      // `fromCache` é a resposta guardada pela idempotência) — avisa o cliente
      // em vez de fingir que gerou um novo.
      setDuplicateNotice(
        pixData.isDuplicate || res.data?.fromCache
          ? pixData.message ||
              'Este é o mesmo PIX gerado há pouco — pague com este código (não geramos uma nova cobrança).'
          : null,
      );
      setPixCode(pixData.code);
      setQrImage(pixData.qrCode);
      setOrderId(pixData.orderId);

      // Presente só quando source='app' foi enviado — captura na criação (não
      // no polling de status).
      if (pixData.appLoginNonce && pixData.appOrderId) {
        setAppSession({ orderId: pixData.appOrderId, nonce: pixData.appLoginNonce });
      }
      // 🔒 PIX gerado com sucesso — botão fica bloqueado (QR code substitui o botão)
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: unknown; status?: number };
        message?: string;
        code?: string;
        config?: { timeout?: number; baseURL?: string };
      };

      capturePaymentError(error, 'pix', {
        component: 'PaymentsPix',
        action: 'generate_pix',
        clientId,
        planId,
        additionalInfo: {
          cpf: cpf.replace(/\d/g, '*').slice(0, 3) + '***' + cpf.slice(-2),
          coupon: coupon?.code,
          errorResponse: axiosError?.response?.data,
          errorStatus: axiosError?.response?.status,
          errorMessage: axiosError?.message,
        },
      });

      if (axiosError?.code === 'ERR_NETWORK' || axiosError?.code === 'ECONNABORTED') {
        captureNetworkError(error, '/payments/pix', 'POST', {
          clientId,
          planId,
          additionalInfo: {
            timeout: axiosError?.config?.timeout,
            baseURL: axiosError?.config?.baseURL,
          },
        });
      }

      // Na renovação a recusa vem com mensagem própria do backend (link inválido,
      // expirado, assinatura inelegível) — mostrar na tela em vez do alert genérico.
      // No checkout público, só o cupom que derruba o PIX à vista abaixo do piso
      // do ano pago (`cupom_indisponivel_no_pix`) tem mensagem pro cliente.
      const recusa = axiosError?.response?.data as
        | { message?: string; reason?: string; code?: string }
        | undefined;
      if (cupomNaTelaRef.current !== cupomDoPedido) {
        // Cupom trocado com o pedido em voo: o erro era do cupom antigo e a
        // tela já mostra outro valor. Só solta o botão, abaixo.
      } else if (renewal) {
        setErroPix(recusa?.message || 'Não foi possível gerar o Pix. Tente novamente.');
      } else if ((recusa?.reason ?? recusa?.code) === 'cupom_indisponivel_no_pix' && recusa?.message) {
        setErroPix(recusa.message);
      } else {
        alert('Erro ao gerar Pix. Por favor, tente novamente.');
      }
      // 🔒 Libera botão para nova tentativa em caso de erro
      setIsProcessing(false);
      localStorage.removeItem('payment_processing_pix');
    } finally {
      setLoading(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    }
  };

  const checkPixStatus = async () => {
    if (!orderId) return;

    try {
      const res = await api.get(`/payments/status/${orderId}`);
      // A API encapsula em { success, data } — lendo `res.data.status` direto o
      // polling nunca via a aprovação e só o WebSocket confirmava.
      const status = res.data?.data || res.data;
      if (status?.amount != null) pixAmountRef.current = { orderId, amount: status.amount };
      if (status?.status === 'paid' || status?.paymentStatus === 'approved') {
        if (approvedRef.current) return;
        approvedRef.current = true;
        trackPixPurchase(status.amount);

        // dataLayer - purchase (GTM)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: orderId,
            currency: 'BRL',
            value: parseFormattedPrice(price.total),
            coupon: coupon?.code || '',
            items: [
              {
                item_id: planId,
                item_name: 'Plano',
                price: parseFormattedPrice(price.total),
                quantity: 1,
              },
            ],
          },
        });

        // Envia cópia para n8n (fire-and-forget)
        console.log('[PaymentsPix] Enviando para n8n (polling), planId:', planId);
        sendPurchaseToN8n({
          event: 'purchase',
          transaction_id: orderId!,
          value: parseFormattedPrice(price.total),
          currency: 'BRL',
          coupon: coupon?.code || '',
          items: [
            {
              item_id: planId,
              item_name: 'Plano',
              price: parseFormattedPrice(price.total),
              quantity: 1,
            },
          ],
          payment_method: 'pix',
          plan_id: planId,
          plan_name: window.location.pathname.split('/')[1],
        });

        if (isInApp() && !renewal) {
          void completeInApp();
        } else {
          redirectToApproved();
        }
      }
    } catch (err: unknown) {
      console.error('Erro ao verificar status do Pix:', err);

      const axiosError = err as {
        response?: { data?: unknown; status?: number };
        message?: string;
      };

      // Captura erro de verificação de status. O nº inteiro abre `/pix/<pedido>`:
      // no contexto vai só o final (#626); no `endpoint`, a redação do
      // `captureError` troca o nº por `[REDACTED]`.
      captureNetworkError(err, `/payments/status/${orderId}`, 'GET', {
        component: 'PaymentsPix',
        action: 'check_pix_status',
        orderId: finalDoPedido(orderId),
        clientId: clientIdProp,
        planId,
        additionalInfo: {
          errorResponse: axiosError?.response?.data,
          errorStatus: axiosError?.response?.status,
          errorMessage: axiosError?.message,
        },
      });
    }
  };

  useEffect(() => {
    if (!orderId) return;

    const interval = setInterval(checkPixStatus, 5000);
    return () => clearInterval(interval);
  }, [orderId]);

  useEffect(() => {
    if (!planId || !cpf) return;

    // limpa dados antigos
    setQrImage('');
    setPixCode('');
    setOrderId(null);

    // generatePix();
  }, [planId]);

  // Limpar QR Code quando CPF muda
  useEffect(() => {
    setQrImage('');
    setPixCode('');
    setOrderId(null);
  }, [cpf]);

  // Cupom aplicado (ou removido) depois de gerar o PIX: o QR na tela é do valor
  // antigo. Limpa pra o cliente gerar de novo no valor que a página mostra — e
  // LIBERA o botão: depois de um PIX gerado `isProcessing` segue travado (só o
  // erro e o timer de 2 minutos o soltam), e sem isto o cliente ficaria sem o
  // QR e sem conseguir gerar outro. Não roda na montagem (a trava do
  // localStorage contra clique duplo continua valendo no recarregar).
  const cupomAnteriorRef = useRef<string | null>(coupon?.code ?? null);
  useEffect(() => {
    const atual = coupon?.code ?? null;
    if (cupomAnteriorRef.current === atual) return;
    cupomAnteriorRef.current = atual;

    if (orderId) lembrarPedidoAnterior(orderId);
    setQrImage('');
    setPixCode('');
    setOrderId(null);
    // O erro na tela era do pedido com o cupom antigo.
    setErroPix(null);
    setIsProcessing(false);
    localStorage.removeItem('payment_processing_pix');
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
  }, [coupon?.code]);

  // O QR que saiu da tela continua pagável (5 dias). Se o cliente pagar o
  // código que já tinha copiado, a tela volta para aquele pedido e a
  // confirmação normal (polling + WebSocket do `orderId`) segue dali — sem isto
  // ela ficaria esperando o QR novo e o cliente pagaria os dois.
  //
  // UMA consulta a cada 15s, revezando entre os pedidos: o checkout tem limite
  // de 30 requisições por minuto POR IP e o polling do pedido atual já usa 12.
  useEffect(() => {
    // Do mais recente para o mais antigo: o código que ficou copiado no celular
    // do cliente é o do último QR que saiu da tela.
    const vigiados = pedidosAnteriores.filter((id) => id !== orderId).reverse();
    if (vigiados.length === 0) return;
    let vez = 0;
    const interval = setInterval(async () => {
      const id = vigiados[vez++ % vigiados.length];
      try {
        const res = await api.get(`/payments/status/${id}`);
        const status = res.data?.data || res.data;
        if (status?.status === 'paid' || status?.paymentStatus === 'approved') {
          setOrderId(id);
        }
      } catch {
        // a próxima volta tenta de novo
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [pedidosAnteriores, orderId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    setUtmData({
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmTerm: params.get('utm_term') || '',
      utmContent: params.get('utm_content') || '',
    });
  }, []);

  interface PaymentConfirmedData {
    orderId: string;
    planName: string | null;
    status: string;
    timestamp: string;
  }

  // 🔒 Cleanup: Limpar timeout ao desmontar componente
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Listener para pagamento confirmado via WebSocket
  useEffect(() => {
    // #665: o console vai para o OpenReplay; o nº inteiro do pedido abre /pix/<pedido>
    console.log('🎯 [PaymentsPix] Configurando listener WebSocket para orderId:', finalDoPedido(orderId));
    console.log('🔌 [PaymentsPix] Socket conectado?', socket.connected);
    console.log('🆔 [PaymentsPix] Socket ID:', socket.id);

    // Conectar se não estiver conectado
    if (!socket.connected) {
      console.log('🔄 [PaymentsPix] Conectando socket...');
      socket.connect();
    }

    // O backend só manda o `payment-confirmed` para a room do pedido (#555).
    acompanharPedido(orderId);

    // Listener para conexão
    const onConnect = () => {
      console.log('✅ [PaymentsPix] Socket conectado com ID:', socket.id);
    };
    socket.on('connect', onConnect);

    // Listener para desconexão
    const onDisconnect = () => {
      console.log('❌ [PaymentsPix] Socket desconectado');
    };
    socket.on('disconnect', onDisconnect);

    // Listener para qualquer evento (debug)
    const onAnyEvent = (eventName: string, ...args: unknown[]) => {
      console.log('📡 [PaymentsPix] Evento recebido:', eventName, args.length);
    };
    socket.onAny(onAnyEvent);

    const handlePaymentConfirmed = (data: PaymentConfirmedData) => {
      console.log(
        '🔔 [PaymentsPix] Pagamento confirmado via WebSocket:',
        finalDoPedido(data.orderId),
        '===',
        finalDoPedido(orderId),
        '?',
        String(data.orderId) === String(orderId),
      );

      // Comparar como string para garantir que funcione
      if (String(data.orderId) === String(orderId)) {
        console.log('✅ [PaymentsPix] Pagamento confirmado para este pedido! Redirecionando...');
        if (approvedRef.current) return;
        approvedRef.current = true;
        if (pixAmountRef.current?.orderId === orderId) trackPixPurchase(pixAmountRef.current.amount);

        // dataLayer - purchase (GTM)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: orderId,
            currency: 'BRL',
            value: parseFormattedPrice(price.total),
            coupon: coupon?.code || '',
            items: [
              {
                item_id: planId,
                item_name: data.planName || 'Plano',
                price: parseFormattedPrice(price.total),
                quantity: 1,
              },
            ],
          },
        });

        // Envia cópia para n8n (fire-and-forget)
        console.log('[PaymentsPix] Enviando para n8n (WebSocket), planId:', planId);
        sendPurchaseToN8n({
          event: 'purchase',
          transaction_id: orderId!,
          value: parseFormattedPrice(price.total),
          currency: 'BRL',
          coupon: coupon?.code || '',
          items: [
            {
              item_id: planId,
              item_name: data.planName || 'Plano',
              price: parseFormattedPrice(price.total),
              quantity: 1,
            },
          ],
          payment_method: 'pix',
          plan_id: planId,
          plan_name: window.location.pathname.split('/')[1],
        });

        if (isInApp() && !renewal) {
          void completeInApp();
        } else {
          redirectToApproved();
        }
      } else {
        console.log('⚠️ [PaymentsPix] OrderId não corresponde ao pedido atual');
      }
    };

    socket.on('payment-confirmed', handlePaymentConfirmed);
    console.log('👂 [PaymentsPix] Listener "payment-confirmed" registrado');

    return () => {
      console.log('🧹 [PaymentsPix] Limpando listeners WebSocket');
      socket.off('payment-confirmed', handlePaymentConfirmed);
      // Só os próprios handlers: `off('connect')` sem handler apagaria o que
      // reentra na room do pedido a cada reconexão (lib/socket.ts).
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.offAny(onAnyEvent);
    };
  }, [orderId]);

  return (
    <>
      <div className="flex items-center justify-center flex-col gap-4">
        <img src="/logo-pix.svg" />
        <h2 className="text-center font-bold text-xl">Gere o código Pix para realizar o pagamento</h2>

        {!qrImage && (
          <div className="w-full flex flex-col gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Digite seu CPF"
                value={cpf}
                onChange={handleCpfChange}
                onBlur={handleCpfBlur}
                className={`w-full h-[48px] rounded-md px-3 py-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${getCpfInputClassName()}`}
                aria-describedby={showCpfError ? 'cpf-error' : undefined}
                aria-invalid={showCpfError ? 'true' : 'false'}
              />
              {cpf && isCpfValid && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>

            {showCpfError && (
              <div id="cpf-error" className="text-red-500 text-sm flex items-center gap-1" role="alert">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {cpfError}
              </div>
            )}

            {/* E-mail */}
            <div className="flex flex-col gap-1 w-full">
              <label className="text-base text-[#595D69]">E-mail</label>
              <span className="text-sm text-[#595D69]">
                {renewal
                  ? `Enviaremos as informações da assinatura para este e-mail. Para trocá-lo, ${
                      isInApp() ? 'use o aplicativo' : 'fale com a nossa equipe'
                    }.`
                  : 'Insira o email para receber as informações da assinatura'}
              </span>
              <input
                type="email"
                inputMode="email"
                placeholder="exemplo@email.com"
                readOnly={!!renewal}
                value={email}
                onChange={(e) => {
                  onEmailChange(e.target.value);
                  setEmailError(null);
                }}
                className={`w-full h-[48px] rounded-md px-3 py-2 border ${email && !isValidEmail(email) ? 'border-red-500 bg-red-50' : 'border-[#dadae5]'} ${renewal ? 'bg-gray-50 text-[#595D69]' : ''}`}
              />
              {email && !isValidEmail(email) && (
                <p className="text-red-500 text-sm">Digite um e-mail válido</p>
              )}
              {emailError && (
                <p className="text-red-500 text-sm">{emailError}</p>
              )}
            </div>

            {erroPix && (
              <div className="text-sm p-4 rounded-md bg-red-100 text-red-800 border border-red-400">
                <p>{erroPix}</p>
                {renewal && (
                  <p className="mt-2 text-red-600">
                    {isInApp()
                      ? 'Se o problema continuar, gere um novo link de renovação no aplicativo.'
                      : 'Se o problema continuar, peça um novo link de renovação na conversa do WhatsApp.'}
                  </p>
                )}
              </div>
            )}

            {termos && <QuadroDeTermos termos={termos} />}

            <button
              onClick={generatePix}
              disabled={!isCpfValid || !isValidEmail(email) || loading || isProcessing}
              className={`flex items-center justify-center gap-2 text-white px-6 py-4 rounded-md transition-all duration-300 ${
                isCpfValid && isValidEmail(email) && !loading && !isProcessing
                  ? 'bg-[#018ED5] hover:opacity-90'
                  : 'bg-[#dadae5] cursor-not-allowed'
              }`}
            >
              {loading || isProcessing ? 'Gerando Pix...' : 'Gerar Código Pix'}
            </button>
          </div>
        )}

        {inAppCompleting && (
          <div className="flex items-center justify-center flex-col gap-4 py-6">
            <div className="text-green-500 text-6xl">✓</div>
            <h2 className="text-2xl font-bold text-gray-800 text-center">Pagamento confirmado!</h2>
            <p className="text-sm text-gray-400">Voltando ao aplicativo...</p>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mt-2"></div>
          </div>
        )}

        {qrImage && !inAppCompleting && (
          <>
            {duplicateNotice && (
              <div className="text-sm p-4 rounded-md bg-amber-50 text-amber-900 border border-amber-400">
                {duplicateNotice}
              </div>
            )}
            <div className="flex lg:flex-row flex-col items-center justify-between gap-3">
              {/* Revisão do #665: o OpenReplay gravava o QR (data URL) na
                  sessão. `hidden` vira um quadro vazio no replay; o `key` monta
                  um <img> novo a cada código, porque troca de `src` num <img>
                  já gravado sai sem checar o `hidden` (módulo Img 16.4.7). */}
              <img
                key={qrImage}
                src={qrImage}
                data-openreplay-hidden=""
                className="w-[200px] h-[200px]"
              />

              <ol className="text-[#3F4D54] text-[14px] gap-2 flex flex-col">
                <li>1. Abra o seu App de pagamentos;</li>
                <li>2. Aponte a câmera do seu celular para o QR Code;</li>
                <li>3. Finalize seu pagamento no seu App;</li>
                <li>4. Aguarde alguns minutos até reconhecermos o pagamento.</li>
              </ol>
            </div>
            <div className="w-full">
              <h3 className="text-[#595D69]">Ou copie e cole a chave Pix abaixo</h3>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  disabled
                  value={pixCode}
                  className="border px-2 border-[#DADAE7] rounded-md w-full h-[48px]"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(pixCode);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 3000);
                  }}
                  disabled={copied}
                  className={`lg:flex hidden w-[120px] h-[48px] rounded-md text-white items-center justify-center transition-all duration-300 ${
                    copied ? 'bg-[#0ba168] cursor-default' : 'bg-[#178ed1] hover:opacity-90'
                  }`}
                >
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              <div className="bg-[#e7f1fd] text-center mt-3 py-2 px-4 w-full rounded-md text-[#0168AC]">
                {/* Vencimento real: getExpirationDate() do backend = 5 dias. */}
                <p>O código gerado vale por 5 dias.</p>
              </div>
            </div>
          </>
        )}
      </div>
      {copied && (
        // Acima da barra fixa do celular, que com a linha de frequência chega a ~130px.
        <div className="fixed bottom-[150px] lg:bottom-[100px] w-[90%] left-1/2 transform -translate-x-1/2 bg-[#018ED5] text-white px-4 py-2 rounded-md shadow-lg z-50 text-sm animate-fade-in-out">
          Código Pix copiado para a área de transferência!
        </div>
      )}

      {!inAppCompleting && (
      <div className="w-full lg:hidden flex items-center justify-between gap-3 px-4 py-2 left-0 min-h-[80px] z-50 bg-white border-t border-black/10 fixed bottom-0">
        <div className="min-w-0 flex-1">
          <span>
            {price.cupom && (
              <>
                <img src="/icon-tag.svg" className="inline w-4 h-4" alt="" /> Cupom aplicado
              </>
            )}
          </span>
          {termos ? (
            <PrecoDoRodape rodape={termos.rodape} />
          ) : (
            <>
              <h3 className="font-bold text-3xl">{price.total}</h3>
              {price.detalhe && <p className="text-xs text-[#595D69]">{price.detalhe}</p>}
            </>
          )}
          <h2 className="font-bold text-xs">+ FRETE GRÁTIS</h2>
        </div>
        <div className="shrink-0 max-w-[50%]">
          {qrImage ? (
            <button
              onClick={() => {
                navigator.clipboard.writeText(pixCode);
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
              }}
              disabled={copied}
              className={`px-4 py-4 text-center leading-tight rounded-md flex items-center justify-center gap-2 transition-all duration-300 text-white ${
                copied ? 'bg-[#0ba168] cursor-default' : 'bg-[#018ED5] hover:opacity-90'
              }`}
            >
              {copied ? 'Copiado!' : 'Copiar código Pix'}
            </button>
          ) : (
            <button
              className={`flex items-center justify-center gap-2 text-white px-4 py-4 text-center leading-tight rounded-md transition-all duration-300 ${
                isCpfValid && isValidEmail(email) && !loading && !isProcessing ? 'bg-[#018ED5] hover:opacity-90' : 'bg-[#dadae5] cursor-not-allowed'
              }`}
              disabled={!isCpfValid || !isValidEmail(email) || loading || isProcessing}
              onClick={generatePix}
            >
              {loading && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
              {loading ? 'Gerando QRCode' : 'Gerar QRCode'}
            </button>
          )}
        </div>
      </div>
      )}
    </>
  );
};

export default PaymentsPix;
